import type { CityConfig } from "../city-template/types";

export const visaliaConfig: CityConfig = {
  city: "Visalia",
  state: "California",
  stateAbbr: "CA",
  slug: "visalia",
  route: "/visalia",
  vitalRecordsIcon: "ri-file-text-line",
  lastModified: "2026-06-14",
  title: "Emergency Passport Visalia, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Visalia, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Tulare County and Central Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Visalia, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Visalia and Tulare County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Visalia%20California%20cityscape%20at%20golden%20hour%20with%20Sierra%20Nevada%20mountains%20visible%20in%20the%20distant%20background%2C%20historic%20downtown%20buildings%20with%20Victorian%20architecture%2C%20orange%20groves%20and%20agricultural%20fields%20surrounding%20the%20city%2C%20warm%20sunset%20light%20over%20the%20Central%20Valley%20landscape%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20agricultural%20city%20environment%20with%20warm%20amber%20and%20golden%20tones&width=1800&height=700&seq=visalia-hero-001&orientation=landscape",
  heroImageAlt: "Visalia California cityscape at golden hour with Sierra Nevada mountains and historic downtown",
  geoPosition: "36.3302;-119.2921",
  geoPlacename: "Visalia, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown San Francisco — approximately 3.5–4 hours from Visalia via I-5",
  },
  nearbyCitiesZips: [
    { name: "Visalia", zips: "93277–93292" },
    { name: "Tulare", zips: "93274, 93275" },
    { name: "Porterville", zips: "93257, 93258" },
    { name: "Exeter", zips: "93221" },
    { name: "Hanford", zips: "93230, 93232" },
    { name: "Lemoore", zips: "93245, 93246" },
    { name: "Dinuba", zips: "93618" },
    { name: "Reedley", zips: "93654" },
    { name: "Fresno", zips: "93701–93730" },
    { name: "Clovis", zips: "93611, 93612, 93619" },
    { name: "Delano", zips: "93215, 93216" },
    { name: "Bakersfield", zips: "93301–93314" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Tulare County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fresno", route: "/fresno" },
    { name: "Bakersfield", route: "/bakersfield" },
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "San Francisco", route: "/san-francisco" },
    { name: "San Jose", route: "/san-jose" },
    { name: "Sacramento", route: "/sacramento" },
    { name: "Stockton", route: "/stockton" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Visalia remotely via secure courier. We do not have a walk-in office in Visalia — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Tulare County travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "Serving all of Tulare County",
  serviceAreaIntro: "From the historic downtown to the northeast neighborhoods, from the agricultural outskirts to the city center — Fast Passport Center serves the entire Visalia community. We regularly help travelers from Tulare, Porterville, Exeter, and Hanford get their passports fast.",
  nearbyCitiesIntro: "We serve all Visalia neighborhoods and nearby Central Valley cities including Tulare, Porterville, Exeter, Hanford, Lemoore, and Dinuba. If you are in Tulare County or nearby Kings County, we have you covered.",
  howItWorksIntro: "Most Visalia travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Fresno Yosemite International Airport serving as the closest major airport, Central Valley travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Visalia and Tulare County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Visalia? Here are the most common ones we hear from Tulare County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Tulare County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Elena R.",
      location: "Visalia, CA",
      rating: 5,
      text: "My flight to Mexico was in 2 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "John M.",
      location: "Tulare, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Canada. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for Central Valley residents.",
      service: "Passport Renewal",
    },
    {
      name: "Patricia K.",
      location: "Porterville, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Hawaii. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Daniel S.",
      location: "Hanford, CA",
      rating: 5,
      text: "As a frequent international traveler from the Central Valley, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "visalia-1",
      name: "Visalia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2345 W Noble Ave",
      city: "Visalia",
      state: "CA",
      zip: "93277",
      phone: "(559) 732-4401",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Visalia",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "visalia-2",
      name: "Tulare Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1400 E Tulare Ave",
      city: "Tulare",
      state: "CA",
      zip: "93274",
      phone: "(559) 686-2317",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Tulare",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Tulare residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "visalia-3",
      name: "Porterville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1050 W Henderson Ave",
      city: "Porterville",
      state: "CA",
      zip: "93257",
      phone: "(559) 781-1954",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Porterville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "visalia-4",
      name: "Hanford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Irwin St",
      city: "Hanford",
      state: "CA",
      zip: "93230",
      phone: "(559) 582-4141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Hanford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kings County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "visalia-5",
      name: "Fresno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2309 Tulare St",
      city: "Fresno",
      state: "CA",
      zip: "93721",
      phone: "(559) 264-3051",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Fresno",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Fresno County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "visalia-6",
      name: "Tulare County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "221 S Mooney Blvd",
      city: "Visalia",
      state: "CA",
      zip: "93291",
      phone: "(559) 636-5000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from the Visalia courthouse",
      website: "https://www.co.tulare.ca.us/",
      websiteLabel: "Tulare County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Central Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Tulare County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Visalia applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Tulare County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Tulare County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Visalia." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Visalia address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Visalia before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Visalia for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Valley travelers." },
    { q: "How fast can I renew my passport in Visalia?", a: "Fast Passport Center offers fast passport renewal in Visalia in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Visalia in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Visalia?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Visalia residents." },
    { q: "Can I expedite a child passport in Visalia?", a: "Yes. Fast Passport Center expedites child passports in Visalia for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Visalia with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Visalia?",
      a: "With Fast Passport Center's emergency courier service, Visalia travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to a passport agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Visalia?",
      a: "Popular options include the Visalia Post Office (93277), Tulare Post Office (93274), Porterville Post Office (93257), and Tulare County Clerk (93291). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Visalia?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Tulare, Porterville, or Hanford?",
      a: "We serve all of Tulare County including Tulare, Porterville, Exeter, and Dinuba. We also serve neighboring Kings County including Hanford and Lemoore. Our overnight shipping labels work from any UPS location, so your location within the Central Valley doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Visalia?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Visalia?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Visalia, Tulare, and Porterville offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Visalia?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Visalia?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Tulare County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Visalia", address: "4000 S Mooney Blvd, Visalia, CA 93277", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Visalia", address: "1730 S Mooney Blvd, Visalia, CA 93277", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Visalia", address: "3140 S Mooney Blvd, Visalia, CA 93277", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Tulare", address: "1250 E Tulare Ave, Tulare, CA 93274", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Porterville", address: "1345 W Henderson Ave, Porterville, CA 93257", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Hanford", address: "106 N 11th Ave, Hanford, CA 93230", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Visalia", address: "3325 W Caldwell Ave, Visalia, CA 93277", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Fresno", address: "4500 W Shaw Ave, Fresno, CA 93722", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Visalia",
      address: "1715 E Main St, Visalia, CA 93292",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Tulare",
      address: "250 E Tulare Ave, Tulare, CA 93274",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Hanford",
      address: "425 N 11th Ave, Hanford, CA 93230",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Fresno",
      address: "655 W Olive Ave, Fresno, CA 93728",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Fresno County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "FAT",
      name: "Fresno Yosemite International Airport",
      distance: "45 miles from Visalia",
      address: "5175 E Clinton Way, Fresno, CA 93727",
      note: "Major regional airport for the Central Valley. International flights to Mexico and connections to worldwide destinations.",
      flightUrl: "https://www.fresno Yosemite.com/",
      flightLabel: "Fresno Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "200 miles from Visalia",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "230 miles from Visalia",
      address: "San Francisco, CA 94128",
      note: "Major international hub for Northern California. Full international flight options.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Tulare County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Tulare County",
      url: "https://www.co.tulare.ca.us/government/county-clerk-recorder",
    },
    {
      label: "Tulare County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Tulare County",
      url: "https://www.tulare.courts.ca.gov/",
    },
  ],
};